/**
 * Typed clients for platform operations (docs/API.md "Platform"): the company lifecycle and onboarding,
 * billing, credits and budgets, sign-in policy, invites and the audit log (console, `/api/v1`), self-serve
 * sign-up and login options (public), and the operator console (`/ops/v1`).
 */
import type {
  AnalyticsExtras, AnalyticsView, AuditEntryView, AuthPolicyInput, AuthPolicyView, BillingView, BudgetLimitInput, BudgetView, CheckoutInput, CompanyMembership, ControlAuditView, CreditPurchaseInput,
  GrantInput, GrantView, IncidentView, LifecycleInput, LifecycleView, LoginOptions, OnboardingView, OperatorAlertSettings, OperatorView, OpsAlarmView, OpsCompanyRow, OpsCreditInput,
  OpsLifecycleInput, OpsOverview, SignupInput, SignupOptions, SignupResult, SubProcessor, EngineView, OpsSwitchesView, OpsSwitchInput, StepControlInput,
} from '@takeit4me/shared'
import { Http, type HttpOptions } from './http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
export type LifecycleBody = In<typeof LifecycleInput>
export type AuthPolicyBody = In<typeof AuthPolicyInput>
export type BudgetLimitBody = In<typeof BudgetLimitInput>
export type CheckoutBody = In<typeof CheckoutInput>
export type CreditPurchaseBody = In<typeof CreditPurchaseInput>
export type SignupBody = In<typeof SignupInput>
export type GrantBody = In<typeof GrantInput>
export type OpsLifecycleBody = In<typeof OpsLifecycleInput>
export type OpsCreditBody = In<typeof OpsCreditInput>
export type OpsSwitchBody = In<typeof OpsSwitchInput>
export type StepControlBody = In<typeof StepControlInput>

export interface AuditQuery { before?: string | null; actor_kind?: string; action?: string; limit?: number }
export interface AuditPage { items: AuditEntryView[]; next_cursor: string | null }
export interface DeadLetterView { id: string; company_id: string | null; job: Record<string, unknown>; attempts: number | null; dead_at: string; resolved_at: string | null; resolved_by: string | null }

/** Platform operations inside a company's console (admins, unless noted). */
export interface PlatformConsoleApi {
  /** Every company this sign-in has a seat in, for the company switcher (any role). */
  companies(): Promise<CompanyMembership[]>
  onboarding(): Promise<OnboardingView>
  setLifecycle(body: LifecycleBody): Promise<LifecycleView>
  billing(): Promise<BillingView>
  /** Starts a Stripe Checkout for a plan; open the returned URL. */
  billingCheckout(body: CheckoutBody): Promise<{ url: string }>
  /** Opens Stripe's billing portal (payment method, invoices, plan changes). */
  billingPortal(): Promise<{ url: string }>
  buyCredits(body: CreditPurchaseBody): Promise<{ url: string }>
  setBudget(body: BudgetLimitBody): Promise<BudgetView[]>
  authPolicy(): Promise<AuthPolicyView>
  setAuthPolicy(body: AuthPolicyBody): Promise<AuthPolicyView>
  /** Sends (or re-sends) the sign-in invite for a staff member. */
  inviteStaff(id: string): Promise<{ ok: boolean; detail: string }>
  auditLog(q?: AuditQuery): Promise<AuditPage>
  /** Sites for the Analytics filter (Analytics space). */
  analyticsSites(): Promise<{ id: string; name: string }[]>
}

export class PlatformClient implements PlatformConsoleApi {
  private http: Http
  constructor(opts: HttpOptions) { this.http = new Http({ ...opts, baseUrl: `${opts.baseUrl.replace(/\/$/, '')}/api/v1` }) }
  companies() { return this.http.get<CompanyMembership[]>('/companies') }
  onboarding() { return this.http.get<OnboardingView>('/onboarding') }
  setLifecycle(body: LifecycleBody) { return this.http.post<LifecycleView>('/lifecycle', body) }
  billing() { return this.http.get<BillingView>('/billing') }
  billingCheckout(body: CheckoutBody) { return this.http.post<{ url: string }>('/billing/checkout', body) }
  billingPortal() { return this.http.post<{ url: string }>('/billing/portal', {}) }
  buyCredits(body: CreditPurchaseBody) { return this.http.post<{ url: string }>('/billing/credits', body) }
  setBudget(body: BudgetLimitBody) { return this.http.put<BudgetView[]>('/billing/budgets', body) }
  authPolicy() { return this.http.get<AuthPolicyView>('/security') }
  setAuthPolicy(body: AuthPolicyBody) { return this.http.put<AuthPolicyView>('/security', body) }
  inviteStaff(id: string) { return this.http.post<{ ok: boolean; detail: string }>(`/staff/${encodeURIComponent(id)}/invite`, {}) }
  auditLog(q: AuditQuery = {}) { return this.http.get<AuditPage>('/audit', q as Record<string, string | number | undefined>) }
  analyticsSites() { return this.http.get<{ id: string; name: string }[]>('/analytics/sites') }
}

/** Public endpoints: no sign-in. `baseUrl` is the router (or a cell when there is only one). */
export interface PublicApi {
  signupOptions(region?: string): Promise<SignupOptions>
  slugAvailable(slug: string, region?: string): Promise<{ available: boolean; reason: string | null }>
  signup(body: SignupBody): Promise<SignupResult>
  loginOptions(q: { company?: string | null; email?: string | null }): Promise<LoginOptions>
  /** The published sub-processor list and the current terms version (17.4-01). */
  subprocessors(): Promise<{ terms_version: string; subprocessors: SubProcessor[] }>
}
export class PublicClient implements PublicApi {
  private http: Http
  constructor(opts: HttpOptions) { this.http = new Http(opts) }
  signupOptions(region = 'au') { return this.http.get<SignupOptions>('/signup/v1/options', { region }) }
  slugAvailable(slug: string, region = 'au') { return this.http.get<{ available: boolean; reason: string | null }>(`/signup/v1/slug/${encodeURIComponent(slug)}`, { region }) }
  signup(body: SignupBody) { return this.http.post<SignupResult>('/signup/v1/companies', body) }
  loginOptions(q: { company?: string | null; email?: string | null }) { return this.http.get<LoginOptions>('/public/v1/login-options', q) }
  subprocessors() { return this.http.get<{ terms_version: string; subprocessors: SubProcessor[] }>('/public/v1/subprocessors') }
}

/**
 * The operator console (design 4, 16.3): takeit4me's own team, signed in through single sign-on
 * (Cloudflare Access). Company details need an active, audited, time-limited grant.
 */
export interface OpsApi {
  me(): Promise<OperatorView>
  overview(): Promise<OpsOverview>
  companies(): Promise<OpsCompanyRow[]>
  grants(all?: boolean): Promise<GrantView[]>
  requestGrant(body: GrantBody): Promise<GrantView>
  endGrant(id: string): Promise<{ ok: true }>
  /**
   * Open alarms: the platform's own (company null) always; inside companies, a region grant shows every company and a
   * company grant that company. `history` lists the last 30 that cleared instead.
   */
  alarms(q?: { history?: boolean }): Promise<OpsAlarmView[]>
  /** "Got it" on a platform alarm: its incident ladder stops; it stays listed until it clears. */
  ackAlarm(id: string): Promise<{ ok: true; acknowledged: boolean }>
  /** The per-kind alert matrix, the incident ladder and the anomaly and model-health thresholds. */
  alertSettings(): Promise<OperatorAlertSettings>
  saveAlertSettings(body: OperatorAlertSettings): Promise<OperatorAlertSettings>
  incidents(all?: boolean): Promise<IncidentView[]>
  resolveIncident(id: string): Promise<{ ok: true }>
  controlAudit(q?: { company_id?: string; before?: string }): Promise<ControlAuditView[]>
  /** A company's own audit log (needs a grant for that company or its region). */
  companyAudit(companyId: string, q?: AuditQuery): Promise<AuditPage>
  setCompanyLifecycle(companyId: string, body: OpsLifecycleBody): Promise<{ ok: true }>
  /** The company's own measures (needs a grant for that company or its region). */
  companyAnalytics(companyId: string, q?: { from?: string; to?: string; channel?: string }): Promise<AnalyticsView & AnalyticsExtras>
  adjustCredits(companyId: string, body: OpsCreditBody): Promise<{ ok: boolean; balance: number }>
  deadLetters(): Promise<DeadLetterView[]>
  settleDeadLetter(id: string, action: 'retry' | 'resolve'): Promise<{ ok: true }>
  /** The platform and this region's kill switches, and whether they were read from the control plane or the cell's copy. */
  switches(): Promise<OpsSwitchesView>
  /** Pause or resume all sending on the platform or in this region, with a reason (admin operators). */
  setSwitch(body: OpsSwitchBody): Promise<OpsSwitchesView>
  /** Every scheduled step and job: its switch (live, dry run, off) and its last run, success and error. */
  engine(): Promise<EngineView>
  /** Switch one step off, into a dry run, or back to live, with a reason (admin operators). */
  setStep(step: string, body: StepControlBody): Promise<EngineView>
}
export class OpsClient implements OpsApi {
  private http: Http
  constructor(opts: HttpOptions) { this.http = new Http({ ...opts, baseUrl: `${opts.baseUrl.replace(/\/$/, '')}/ops/v1` }) }
  me() { return this.http.get<OperatorView>('/me') }
  overview() { return this.http.get<OpsOverview>('/overview') }
  companies() { return this.http.get<OpsCompanyRow[]>('/companies') }
  grants(all = false) { return this.http.get<GrantView[]>('/grants', { all: all ? 1 : undefined }) }
  requestGrant(body: GrantBody) { return this.http.post<GrantView>('/grants', body) }
  endGrant(id: string) { return this.http.delete<{ ok: true }>(`/grants/${encodeURIComponent(id)}`) }
  alarms(q: { history?: boolean } = {}) { return this.http.get<OpsAlarmView[]>('/alarms', { history: q.history ? 1 : undefined }) }
  ackAlarm(id: string) { return this.http.post<{ ok: true; acknowledged: boolean }>(`/alarms/${encodeURIComponent(id)}/ack`, {}) }
  alertSettings() { return this.http.get<OperatorAlertSettings>('/alert-settings') }
  saveAlertSettings(body: OperatorAlertSettings) { return this.http.put<OperatorAlertSettings>('/alert-settings', body) }
  incidents(all = false) { return this.http.get<IncidentView[]>('/incidents', { all: all ? 1 : undefined }) }
  resolveIncident(id: string) { return this.http.post<{ ok: true }>(`/incidents/${encodeURIComponent(id)}/resolve`, {}) }
  controlAudit(q: { company_id?: string; before?: string } = {}) { return this.http.get<ControlAuditView[]>('/audit', q) }
  companyAudit(companyId: string, q: AuditQuery = {}) { return this.http.get<AuditPage>(`/companies/${encodeURIComponent(companyId)}/audit`, q as Record<string, string | number | undefined>) }
  setCompanyLifecycle(companyId: string, body: OpsLifecycleBody) { return this.http.post<{ ok: true }>(`/companies/${encodeURIComponent(companyId)}/lifecycle`, body) }
  companyAnalytics(companyId: string, q: { from?: string; to?: string; channel?: string } = {}) { return this.http.get<AnalyticsView & AnalyticsExtras>(`/companies/${encodeURIComponent(companyId)}/analytics`, q) }
  adjustCredits(companyId: string, body: OpsCreditBody) { return this.http.post<{ ok: boolean; balance: number }>(`/companies/${encodeURIComponent(companyId)}/credits`, body) }
  deadLetters() { return this.http.get<DeadLetterView[]>('/dead-letters') }
  settleDeadLetter(id: string, action: 'retry' | 'resolve') { return this.http.post<{ ok: true }>(`/dead-letters/${encodeURIComponent(id)}/${action}`, {}) }
  switches() { return this.http.get<OpsSwitchesView>('/switches') }
  setSwitch(body: OpsSwitchBody) { return this.http.post<OpsSwitchesView>('/switches', body) }
  engine() { return this.http.get<EngineView>('/engine') }
  setStep(step: string, body: StepControlBody) { return this.http.put<EngineView>(`/engine/steps/${encodeURIComponent(step)}`, body) }
}
